import { useNavigate } from 'react-router-dom';
import { useMutation } from "@tanstack/react-query";
import { CirclePlus, Trash2, User, Pencil } from 'lucide-react';
import { useState } from 'react';

const Join = () => {
	const API_URL = import.meta.env.VITE_API_URL;
	const navigate = useNavigate();

	// 계정
	const [form, setForm] = useState({id: "", password: "", passwordConfirm: "", agreeTerms: false, agreePrivacy: false});
	const [formErrors, setFormErrors] = useState({id: "", password: "", passwordConfirm: "", agreeTerms: "", agreePrivacy: ""});
	const [isIdChecked, setIsIdChecked] = useState(false);

	// 부모
	const [parents, setParents] = useState([{name: "", email: "", phone: ""}]);
	const [parentsErrors, setParentsErrors] = useState([{name: "", email: "", phone: ""}]);
	const [editParentIndex, setEditParentIndex] = useState(0);

	// 자녀
	const [children, setChildren] = useState([{name: "", birth: "", gender: ""}]);
	const [childrenErrors, setChildrenErrors] = useState([{name: "", birth: "", gender: ""}]);
	const [editChildIndex, setEditChildIndex] = useState(0);

	// 동의
	const [openTerms, setOpenTerms] = useState({terms: false, privacy: false});
	const isAllChecked = form.agreeTerms && form.agreePrivacy;

	// 회원가입
	const [joinError, setJoinError] = useState("");	

	const joinMutation = useMutation({
		mutationFn: async (data) => {
			const params = new URLSearchParams();
			params.append("id", data.id);
			params.append("password", data.password);
			params.append("passwordConfirm", data.passwordConfirm);
			params.append("agreeTerms", data.agreeTerms ? "Y" : "N");
			params.append("agreePrivacy", data.agreePrivacy ? "Y" : "N");
			params.append("parents", JSON.stringify(data.parents));
			params.append("children", JSON.stringify(data.children));

			const response = await fetch(`${API_URL}/bbs/ajax.join.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});
			
			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						setJoinError("필수 필드값이 입력되지 않았습니다.");
						break;
					case "DUPLICATE_MEMBER":
						setIsIdChecked(false);
						setFormErrors((prev) => ({...prev, id: "이미 사용중인 아이디입니다."}));
						break;
					default :
						setJoinError("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(!response.data) {
				setJoinError("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if(response.data.memberId) {
				alert("회원가입이 완료되었습니다.");
				navigate("/member/login");
			}
		},
		onError: (error: any) => {
			setJoinError(error.message);
		}
	});

	const checkIdMutation = useMutation({
		mutationFn: async (data: { id: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.check_id.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});

			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				setIsIdChecked(false);
				switch (response.code) {
					case "INVALID_REQUEST":
						setFormErrors((prev) => ({...prev, id: "아이디를 입력해주세요."}));
						break;
					case "DUPLICATE_MEMBER":
						setFormErrors((prev) => ({...prev, id: "이미 사용중인 아이디입니다."}));
						break;
					default:
						setFormErrors((prev) => ({...prev, id: "알 수 없는 오류가 발생했습니다."}));
						break;
				}
				return;
			}

			if(!response.data) {
				setFormErrors((prev) => ({...prev, id: "알 수 없는 오류가 발생했습니다."}));
				return;
			}

			if(response.data.memberAvailable) {
				setIsIdChecked(true);
				setFormErrors((prev) => ({ ...prev, id: "" }));
			}
		},
		onError: (error: any) => {
			setIsIdChecked(false);
			setFormErrors((prev) => ({...prev, id: error.message}));
		}
	});

	const validate = (name: string, value: any, form: any) => {
		let validValue = value;
		let errorMsg = "";

		if (name === "id") {
			if (!value.trim()) {
				errorMsg = "아이디를 입력해주세요.";
			} else if (/[^a-zA-Z0-9]/.test(value)) {
				errorMsg = "아이디는 영문, 숫자만 입력 가능합니다.";
			} else if (value.length < 3) {
				errorMsg = "아이디는 3자 이상 입력해주세요.";
			}
		}

		if (name === "password") {
			if (!value.trim()) {
				errorMsg = "비밀번호를 입력해주세요.";
			} else if (value.length < 4) {
				errorMsg = "비밀번호는 4자 이상 입력해주세요.";
			}
			validValue = value.replace(/\s/g, "");
		}

		if (name === "passwordConfirm") {
			if (!value.trim()) {
				errorMsg = "비밀번호 확인을 입력해주세요.";
			} else if (value !== form.password) {
				errorMsg = "비밀번호가 일치하지 않습니다.";
			}
		}

		return {
			value: validValue,
			error: errorMsg,
		};
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;

		if (name === "id") setIsIdChecked(false);

		const inputValue = type === "checkbox" ? checked : value;

		const result = validate(name, inputValue, form);

		setJoinError("");
		setForm(prev => ({ ...prev, [name]: result.value }));
		setFormErrors(prev => ({ ...prev, [name]: result.error }));
	};

	const parentValidate = (field, value) => {
		let validValue = value;
		let errorMsg = "";

		if (field === "name") {
			validValue = value.replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g, "");
			if (!validValue.trim()) {
				errorMsg = "이름을 입력해주세요.";
			}
		}

		if (field === "email") {
			validValue = value.trim();
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(validValue)) {
				errorMsg = "올바른 이메일 형식이 아닙니다.";
			}
		}

		if (field === "phone") {
			let numbers = value.replace(/[^0-9]/g, "");
			if (numbers.length < 4) {
				validValue = numbers;
			} else if (numbers.length < 8) {
				validValue = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
			} else {
				validValue = `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
			}
			if (numbers.length < 10) {
				errorMsg = "올바른 연락처를 입력해주세요.";
			}
		}

		return {
			value: validValue,
			error: errorMsg,
		};
	}

	const handleAddParent = () => {
		if (parents.length >= 2) {
			alert("보호자 정보는 최대 2명까지만 등록 가능합니다.");
			return;
		}

		let allValid = true;

		const validatedErrors = parents.map((p) => {
			const nameCheck = parentValidate("name", p.name);
			const emailCheck = parentValidate("email", p.email);
			const phoneCheck = parentValidate("phone", p.phone);

			if (nameCheck.error || emailCheck.error || phoneCheck.error) {
				allValid = false;
			}

			return {
				name: nameCheck.error,
				email: emailCheck.error,
				phone: phoneCheck.error,
			};
		});

		setParentsErrors(validatedErrors);

		if(!allValid) return;

		setParents([...parents,	{name: '', email: '', phone: ''}]);
		setParentsErrors([...parentsErrors, {name: "", email: "", phone: ""}]);
		setEditParentIndex(parents.length);
	}

	const handleParentEdit = (index) => {
		setEditParentIndex(index);
	}

	const handleParentComplete = (index) => {
		const parent = parents[index];
		const nameCheck = parentValidate("name", parent.name);
		const emailCheck = parentValidate("email", parent.email);
		const phoneCheck = parentValidate("phone", parent.phone);
		setParentsErrors((prev) => {
			const newErrors = [...prev];
			newErrors[index] = {name: nameCheck.error, email: emailCheck.error, phone: phoneCheck.error};
			return newErrors;
		});
		if(parent.name && parent.email && parent.phone && !nameCheck.error && !emailCheck.error && !phoneCheck.error) {
			setEditParentIndex(-1);
		}
	}

	const handleParentChange = (index, field, value) => {
		const { value: validValue, error: errorMsg } = parentValidate(field, value);

		const newParents = [...parents];
		newParents[index] = {...newParents[index], [field]: validValue};
		setParents(newParents);

		const newErrors = [...parentsErrors];
		newErrors[index] = { ...newErrors[index], [field]: errorMsg };
		setParentsErrors(newErrors);
	};

	const handleParentDelete = (index) => {
		if (parents.length <= 1) {
			alert("최소 한 명의 보호자 정보는 입력해야 합니다.");
			return;
		}

		const newParents = parents.filter((_, i) => i !== index);
		const newErrors = parentsErrors.filter((_, i) => i !== index);

		setParents(newParents);
		setParentsErrors(newErrors);

		setEditParentIndex(-1);
	}

	const childValidate = (field, value) => {
	    let validValue = value;
	    let errorMsg = "";

	    if (field === "name") {
			validValue = value.replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g, "");
			if (!validValue.trim()) {
				errorMsg = "이름을 입력해주세요.";
			}
		}

		if (field === "birth") {
			let numbers = value.replace(/[^0-9]/g, "").slice(0, 8);
			if (numbers.length < 5) {
				validValue = numbers;
			} else if (numbers.length < 7) {
				validValue = `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
			} else {
				validValue = `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6)}`;
			}

			if(!numbers) {
				errorMsg = "생년월일을 입력해주세요.";
			} else if (numbers.length === 8) {
				const year = Number(numbers.slice(0, 4));
				const month = Number(numbers.slice(4, 6));
				const day = Number(numbers.slice(6, 8));

				if (month < 1 || month > 12 || day < 1 || day > 31) {
					errorMsg = "올바른 생년월일을 입력해주세요.";
				}
			} else {
        		errorMsg = "올바른 생년월일을 입력해주세요.";
    		}
		}

	    if (field === "gender") {
	        validValue = value;
	        if (!validValue) errorMsg = "성별을 선택해주세요.";
	    }

	    return { 
	    	value: validValue, 
	    	error: errorMsg 
	    };
	};

	const handleChildChange = (index, field, value) => {
	    const { value: validValue, error: errorMsg } = childValidate(field, value);

	    const newChildren = [...children];
        newChildren[index] = { ...newChildren[index], [field]: validValue };
        setChildren(newChildren);

        const newErrors = [...childrenErrors];
        newErrors[index] = { ...newErrors[index], [field]: errorMsg };
        setChildrenErrors(newErrors);
	};

	const handleAddChild = () => {
	    if (children.length >= 4) {
			alert("자녀 정보는 최대 4명까지만 등록 가능합니다.");
			return;
		}

	    let allValid = true;
	    const validatedErrors = children.map((c) => {
	        const n = childValidate("name", c.name);
	        const b = childValidate("birth", c.birth);
	        const g = childValidate("gender", c.gender);
	        if (n.error || b.error || g.error) allValid = false;
	        return { name: n.error, birth: b.error, gender: g.error };
	    });

	    setChildrenErrors(validatedErrors);
	    if (!allValid) return;

	    setChildren([...children, { name: "", birth: "", gender: "" }]);
	    setChildrenErrors([...validatedErrors, { name: "", birth: "", gender: "" }]);
	    setEditChildIndex(children.length);
	};

	const handleChildComplete = (index) => {
	    const child = children[index];
	    const n = childValidate("name", child.name);
	    const b = childValidate("birth", child.birth);
	    const g = childValidate("gender", child.gender);

	    if (child.name && child.birth && child.gender && !n.error && !b.error && !g.error) {
	        setEditChildIndex(-1);
	    }
	};

	const handleChildDelete = (index) => {
	    if (children.length <= 1) {
	        alert("최소 한 명의 자녀 정보는 입력해야 합니다.");
	        return;
	    }
	    setChildren(children.filter((_, i) => i !== index));
	    setChildrenErrors(childrenErrors.filter((_, i) => i !== index));
	    setEditChildIndex(-1);
	};

	const handleCheckId = () => {
		checkIdMutation.mutate({id: form.id});
	}

	const handleAllAgree = (e) => {
		const checked = e.target.checked;
		setForm((prev) => ({...prev, agreeTerms: checked, agreePrivacy: checked}));
	};

	const handleJoin = () => {
	    // 계정
	    const accountFields = ["id", "password", "passwordConfirm"];
	    const newAccountErrors = {};
	    let isAccountValid = true;

	    accountFields.forEach((field) => {
	        const result = validate(field, form[field], form); // 기존 validate 함수 재사용
	        newAccountErrors[field] = result.error;
	        if (result.error) isAccountValid = false;
	    });

	    // 부모
	    let isParentsValid = true;
	    const newParentsErrors = parents.map((p) => {
	        const n = parentValidate("name", p.name);
	        const e = parentValidate("email", p.email);
	        const ph = parentValidate("phone", p.phone);
	        
	        if (n.error || e.error || ph.error) isParentsValid = false;
	        return { name: n.error, email: e.error, phone: ph.error };
	    });

	    // 자녀
	    let isChildrenValid = true;
	    const newChildrenErrors = children.map((c) => {
	        const n = childValidate("name", c.name);
	        const b = childValidate("birth", c.birth);
	        const g = childValidate("gender", c.gender);
	        
	        if (n.error || b.error || g.error) isChildrenValid = false;
	        return { name: n.error, birth: b.error, gender: g.error };
	    });

	    setFormErrors(newAccountErrors);
	    setParentsErrors(newParentsErrors);
	    setChildrenErrors(newChildrenErrors);

	    if (!form.agreeTerms || !form.agreePrivacy) {
	        alert("필수 약관에 동의해주세요.");
	        return;
	    }

	    if (!isAccountValid || !isParentsValid || !isChildrenValid) {
	        if (!isParentsValid) setEditParentIndex(newParentsErrors.findIndex(e => e.name || e.email || e.phone));
	        else if (!isChildrenValid) setEditChildIndex(newChildrenErrors.findIndex(e => e.name || e.birth || e.gender));
	        alert("입력 정보를 다시 확인해주세요.");
	        return;
	    }

	    if (!isIdChecked) {
	        setFormErrors((prev) => ({ ...prev, id: "아이디 중복확인을 해주세요." }));
	        alert("아이디 중복확인을 해주세요.");
	        return;
	    }

	    const finalData = {
	        ...form,
	        parents: parents,
	        children: children
	    };

	    joinMutation.mutate(finalData);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleJoin();
	};

	return (
		<section id="join" className="min-h-[80vh] flex items-center py-12 md:py-16 bg-gradient-to-br from-purple-600 to-blue-600">
			<div className="max-w-7xl mx-auto px-4 w-full">
				<div className="max-w-2xl mx-auto">
					<div className="text-center mb-10">
						<h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
							회원가입
						</h2>
						<p className="text-white/80 mt-2 text-sm">
							서비스를 이용하려면 회원가입해주세요
						</p>
					</div>
					<div className="bg-white/95 backdrop-blur rounded-2xl p-6 md:p-8 shadow-xl border border-white/20">
						<div className="space-y-5">
							<div>
								<label htmlFor="id" className="block text-sm font-semibold text-gray-700 mb-1">
									아이디
								</label>
								<div className="flex flex-col sm:flex-row gap-2">
									<input
										type="text"
										id="id"
										name="id"
										value={form.id}
										onChange={handleChange}
										disabled={joinMutation.isPending || isIdChecked}
										placeholder="아이디를 입력해주세요"
										className={`w-full sm:flex-1 px-4 py-3 rounded-lg border text-sm transition
											${formErrors.id
												? "border-red-500 focus:ring-red-500 focus:border-red-500"
												: "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
										`}
									/>
									<button
										type="button"
										onClick={handleCheckId}
										disabled={!form.id || joinMutation.isPending || isIdChecked}
										className={`w-full sm:w-auto px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap
											${
												isIdChecked
													? "bg-green-500 text-white cursor-default"
													: !form.id || joinMutation.isPending
													? "bg-gray-200 text-gray-400 cursor-not-allowed"
													: "bg-purple-600 text-white hover:bg-purple-700"
											}
										`}
									>
										{isIdChecked ? "확인완료" : "중복확인"}
									</button>
								</div>
								{formErrors.id && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.id}
									</p>
								)}
							</div>
							<div>
								<label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
									비밀번호
								</label>
								<input
									type="password"
									id="password"
									name="password"
									value={form.password}
									onChange={handleChange}
									disabled={joinMutation.isPending}
									placeholder="비밀번호를 입력해주세요"
									className={`w-full px-4 py-3 rounded-lg border text-sm transition
      									${formErrors.password ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
							      	`}
								/>
								{formErrors.password && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.password}
									</p>
								)}
							</div>
							<div>
								<label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
									비밀번호 확인
								</label>
								<input
									type="password"
									id="passwordConfirm"
									name="passwordConfirm"
									value={form.passwordConfirm}
									onChange={handleChange}
									disabled={joinMutation.isPending}
									placeholder="비밀번호를 입력해주세요"
									className={`w-full px-4 py-3 rounded-lg border text-sm transition
      									${formErrors.passwordConfirm ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
							      	`}
								/>
								{formErrors.passwordConfirm && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.passwordConfirm}
									</p>
								)}
							</div>
							<div className="grid md:grid-cols-2 gap-6">
								<div className="bg-gray-50 rounded-xl p-5 border">
									<div className="flex items-center gap-2 mb-3">
										<h3 className="text-sm font-semibold text-gray-500">
											부모 / 보호자
										</h3>
									</div>
									<div className="space-y-4">
										{parents.map((parent, index) => (
											<div key={index} className="space-y-4">
												{editParentIndex !== index ? (
													<div 
														onClick={() => handleParentEdit(index)}
														className="relative p-5 border rounded-xl bg-gray-50 flex flex-col items-end flex-col cursor-pointer hover:border-blue-400 transition-colors group"
													>
														<div className="flex space-x-3 mb-1">
															<button 
																className="text-xs text-blue-500 hover:text-blue-700 font-medium"
																onClick={(e) => {
													                e.stopPropagation();
													                handleParentEdit(index);
													            }}
													        >
													        	수정
													        </button>
													        <button 
													        	className="text-xs text-red-500 hover:text-red-700 font-medium"
													        	onClick={(e) => {
													                e.stopPropagation();
													                handleParentDelete(index);
													            }}
													        >
													        	삭제
													        </button>
													    </div>
													    <div className="flex items-center justify-center w-full">
													    	<div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm mr-3">
														    	<User className="w-6 h-6 text-gray-400" />
														    </div>
														    <div className="flex-1 min-w-0">
														    	<p className="text-[15px] font-bold text-gray-900 truncate">
														    		{parent.name || "이름 없음"}
														    	</p>
														    	<div className="mt-1 space-y-0.5">
														    		<p className="text-xs text-gray-500 flex items-center">
														    			<span className="w-12 text-gray-400 uppercase tracking-tighter flex-shrink-0">이메일</span>
														    			<span className="truncate">{parent.email || "-"}</span>
														    		</p>
														    		<p className="text-xs text-gray-500 flex items-center">
														    			<span className="w-12 text-gray-400 tracking-tighter flex-shrink-0">연락처</span>
														    			<span>{parent.phone || "-"}</span>
														    		</p>
														    	</div>
														    </div>
													    </div>
													</div>
												) : (
													<>
														<input
															type="text"
															placeholder="이름을 입력해주세요."
															value={parent.name}
															disabled={joinMutation.isPending}
															onChange={(e) => handleParentChange(index, 'name', e.target.value)}
															className="w-full px-4 py-3 rounded-lg border text-sm"
														/>
														{parentsErrors[index]?.name && (
															<p className="text-red-500 text-xs mt-1 min-h-[16px]">
																{parentsErrors[index]?.name}
															</p>
														)}
														<input
															type="text"
															placeholder="이메일을 입력해주세요."
															value={parent.email}
															disabled={joinMutation.isPending}
															onChange={(e) => handleParentChange(index, 'email', e.target.value)}
															className="w-full px-4 py-3 rounded-lg border text-sm"
														/>
														{parentsErrors[index]?.email && (
															<p className="text-red-500 text-xs mt-1 min-h-[16px]">
																{parentsErrors[index]?.email}
															</p>
														)}
														<input
															type="text"
															placeholder="연락처를 입력해주세요."
															value={parent.phone}
															disabled={joinMutation.isPending}
															onChange={(e) => handleParentChange(index, 'phone', e.target.value)}
															className="w-full px-4 py-3 rounded-lg border text-sm"
														/>
														{parentsErrors[index]?.phone && (
															<p className="text-red-500 text-xs mt-1 min-h-[16px]">
																{parentsErrors[index]?.phone}
															</p>
														)}
														<button
														    type="button"
														    onClick={() => handleParentComplete(index)}
														    disabled={!parent.name || !parent.email || !parent.phone || joinMutation.isPending}
														    className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap mt-2
														        ${
														            parent.name && parent.email && parent.phone && !joinMutation.isPending
														                ? "bg-purple-600 text-white hover:bg-purple-700"
														                : "bg-gray-200 text-gray-400 cursor-not-allowed"
														        }
														    `}
														>
														    입력 완료
														</button>
													</>
												)}
											</div>
										))}
										{parents.length < 2 && (
											<button
												type="button"
												onClick={handleAddParent}
												className="w-full h-12 border-2 border-dashed rounded-xl flex items-center justify-center hover:bg-gray-100"
											>
												<CirclePlus className="text-gray-500" />
											</button>
										)}
									</div>
								</div>
								<div className="bg-gray-50 rounded-xl p-5 border">
									<div className="flex items-center gap-2 mb-3">
										<h3 className="text-sm font-semibold text-gray-500">
											자녀
										</h3>
									</div>
									<div className="space-y-4">
										{children.map((child, index) => (
										    <div key={index} className="space-y-4">
										        {editChildIndex !== index ? (
										            <div 
										                onClick={() => setEditChildIndex(index)}
										                className="relative p-5 border rounded-xl bg-gray-50 flex flex-col items-end cursor-pointer hover:border-purple-400 transition-colors group"
										            >
										                <div className="flex space-x-3 mb-1">
										                    <button className="text-xs text-blue-500 font-medium" onClick={(e) => { e.stopPropagation(); setEditChildIndex(index); }}>수정</button>
										                    <button className="text-xs text-red-500 font-medium" onClick={(e) => { e.stopPropagation(); handleChildDelete(index); }}>삭제</button>
										                </div>
										                <div className="flex items-center justify-center w-full">
										                    <div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm mr-3">
										                        <User className="w-6 h-6 text-gray-400" />
										                    </div>
										                    <div className="flex-1 min-w-0">
										                        <p className="text-[15px] font-bold text-gray-900 truncate">
										                            {child.name || "이름 없음"} ({child.gender === "M" ? "남" : child.gender === "F" ? "여" : "-"})
										                        </p>
										                        <div className="mt-1 space-y-0.5">
										                            <p className="text-xs text-gray-500 flex items-center">
										                                <span className="w-16 text-gray-400 tracking-tighter flex-shrink-0">생년월일</span>
										                                <span>{child.birth || "-"}</span>
										                            </p>
										                        </div>
										                    </div>
										                </div>
										            </div>
										        ) : (
										            <div className="space-y-4">
										                <input
										                    type="text"
										                    placeholder="자녀 이름을 입력해주세요."
										                    value={child.name}
										                    disabled={joinMutation.isPending}
										                    onChange={(e) => handleChildChange(index, 'name', e.target.value)}
										                    className="w-full px-4 py-3 rounded-lg border text-sm"
										                />
										                {childrenErrors[index]?.name && <p className="text-red-500 text-xs mt-1">{childrenErrors[index].name}</p>}

										                <input
										                    type="text"
										                    placeholder="생년월일(8자리)을 입력해주세요."
										                    value={child.birth}
										                    disabled={joinMutation.isPending}
										                    onChange={(e) => handleChildChange(index, 'birth', e.target.value)}
										                    className="w-full px-4 py-3 rounded-lg border text-sm"
										                />
										                {childrenErrors[index]?.birth && <p className="text-red-500 text-xs mt-1">{childrenErrors[index].birth}</p>}

										                <div className="flex w-full rounded-xl border overflow-hidden">
										                    <label className={`flex-1 text-center py-3 cursor-pointer transition text-sm ${child.gender === "M" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
										                        <input type="radio" name={`gender-${index}`} value="M" checked={child.gender === "M"} onChange={(e) => handleChildChange(index, 'gender', e.target.value)} className="hidden" />
										                        남자
										                    </label>
										                    <label className={`flex-1 text-center py-3 cursor-pointer transition text-sm border-l ${child.gender === "F" ? "bg-purple-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
										                        <input type="radio" name={`gender-${index}`} value="F" checked={child.gender === "F"} onChange={(e) => handleChildChange(index, 'gender', e.target.value)} className="hidden" />
										                        여자
										                    </label>
										                </div>
										                {childrenErrors[index]?.gender && <p className="text-red-500 text-xs mt-1">{childrenErrors[index].gender}</p>}

										                <button
										                    type="button"
										                    onClick={() => handleChildComplete(index)}
										                    disabled={!child.name || !child.birth || !child.gender || joinMutation.isPending}
										                    className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition mt-2 
										                    	${child.name && child.birth && child.gender && !joinMutation.isPending 
										                    		? "bg-purple-600 text-white hover:bg-purple-700" 
										                    		: "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
										                >
										                    입력 완료
										                </button>
										            </div>
										        )}
										    </div>
										))}
										{children.length < 4 && (
											<button
												type="button"
												onClick={handleAddChild}
												className="w-full h-12 border-2 border-dashed rounded-xl flex items-center justify-center hover:bg-gray-100"
											>
												<CirclePlus className="text-gray-500" />
											</button>
										)}
									</div>
								</div>
							</div>
							<div className="mt-6 space-y-3">
								<div className="border rounded-lg p-4">
									<div className="flex items-center justify-between">
										<label className="flex items-center gap-3 flex-1 min-w-0 mr-2 cursor-pointer">
											<input
												type="checkbox"
												name="agreeTerms"
												checked={form.agreeTerms}
												onChange={handleChange}
												className="w-5 h-5 cursor-pointer appearance-none border border-gray-400 rounded shrink-0
												checked:bg-purple-600 checked:border-purple-600
												relative
												before:content-[''] before:absolute before:left-[6px] before:top-[2px]
												before:w-[5px] before:h-[10px]
												before:border-white before:border-r-2 before:border-b-2
												before:rotate-45 before:opacity-0
												checked:before:opacity-100"
											/>
											<span className="text-m sm:text-base text-gray-700 truncate min-w-0">
												이용약관 동의 (필수)
											</span>
										</label>
										<button
											type="button"
											onClick={() =>
												setOpenTerms(prev => ({ ...prev, terms: !prev.terms }))
											}
											className="text-xs text-gray-500 hover:text-gray-700 underline shrink-0"
										>
											{openTerms.terms ? "접기" : "보기"}
										</button>
									</div>
									{openTerms.terms && (
										<div className="mt-3 text-xs text-gray-600 bg-gray-50 p-3 rounded-md max-h-40 overflow-y-auto">
											<p>본 서비스는 회원가입 및 서비스 제공을 위해 아래와 같은 약관에 동의가 필요합니다.</p>
											<p className="mt-3">1. 회원은 본인의 정확한 정보를 입력해야 하며, 타인의 정보를 도용할 수 없습니다.</p>
											<p>2. 서비스 이용 중 관련 법령 및 공공질서를 준수해야 합니다.</p>
											<p>3. 회사는 서비스 제공을 위해 필요한 경우 서비스 내용을 변경하거나 중단할 수 있습니다.</p>
											<p>4. 회원의 부주의로 발생한 문제에 대해서는 회사가 책임을 지지 않습니다.</p>
											<p className="mt-3">위 내용을 충분히 이해하였으며, 이에 동의합니다.</p>
										</div>
									)}
								</div>
								<div className="border rounded-lg p-4">
									<div className="flex items-center justify-between">
										<label className="flex items-center gap-2 flex-1 min-w-0 mr-2 cursor-pointer">
											<input
												type="checkbox"
												name="agreePrivacy"
												checked={form.agreePrivacy}
												onChange={handleChange}
												className="w-5 h-5 cursor-pointer appearance-none border border-gray-400 rounded shrink-0
												checked:bg-purple-600 checked:border-purple-600
												relative
												before:content-[''] before:absolute before:left-[6px] before:top-[2px]
												before:w-[5px] before:h-[10px]
												before:border-white before:border-r-2 before:border-b-2
												before:rotate-45 before:opacity-0
												checked:before:opacity-100"
											/>
											<span className="text-m sm:text-base text-gray-700 truncate min-w-0">
												개인정보 수집 및 이용 동의 (필수)
											</span>
										</label>
										<button
											type="button"
											onClick={() =>
												setOpenTerms(prev => ({ ...prev, privacy: !prev.privacy }))
											}
											className="text-xs text-gray-500 hover:text-gray-700 underline shrink-0"
										>
											{openTerms.privacy ? "접기" : "보기"}
										</button>
									</div>
									{openTerms.privacy && (
										<div className="mt-3 text-xs text-gray-600 bg-gray-50 p-3 rounded-md max-h-40 overflow-y-auto">
											<p>회사는 회원가입 및 서비스 제공을 위해 아래와 같이 개인정보를 수집·이용합니다.</p>
											<p className="mt-3">1. 수집 항목</p>
											<p>- 부모/보호자: 이름, 연락처, 이메일</p>
											<p>- 자녀: 이름, 생년월일, 성별</p>
											<p className="mt-3">2. 수집 목적</p>
											<p>- 회원 식별 및 서비스 제공</p>
											<p>- 고객 문의 응대 및 관리</p>
											<p className="mt-3">3. 보유 및 이용 기간</p>
											<p>- 회원 탈퇴 시까지 보관 후 지체 없이 파기</p>
											<p className="mt-3">4. 동의 거부 권리</p>
											<p>- 개인정보 수집에 동의하지 않을 권리가 있으며, 동의하지 않을 경우 서비스 이용이 제한될 수 있습니다.</p>
											<p className="mt-3">위 내용을 충분히 이해하였으며, 이에 동의합니다.</p>
										</div>
									)}
								</div>
								<div className="border rounded-lg p-4">
									<div className="flex items-center justify-between">
										<label className="flex items-center gap-2 cursor-pointer">
											<input
												type="checkbox"
												name="agreePrivacy"
												checked={isAllChecked}
												onChange={handleAllAgree}
												className="w-5 h-5 cursor-pointer appearance-none border border-gray-400 rounded
												checked:bg-purple-600 checked:border-purple-600
												relative
												before:content-[''] before:absolute before:left-[6px] before:top-[2px]
												before:w-[5px] before:h-[10px]
												before:border-white before:border-r-2 before:border-b-2
												before:rotate-45 before:opacity-0
												checked:before:opacity-100"
											/>
											<span className="text-m sm:text-base text-gray-700">
												전체 동의
											</span>
										</label>
									</div>
								</div>
							</div>
							<button
								onClick={handleJoin}
								onKeyDown={handleKeyDown}
								disabled={joinMutation.isPending}
								className={`w-full py-3 rounded-lg font-semibold transition 
    								${joinMutation.isPending ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90"}
								`}
							>
								{joinMutation.isPending ? "회원가입 중..." : "회원가입"}
							</button>
							{joinError && (
								<p className="text-red-500 text-sm mt-3 text-center">
									{joinError}
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

export default Join;